// Page: CreateTemplates
// Purpose: Admin flow to create a standard template (non-video).
// Validates admin access, handles submission, toasts, and navigation on success.
import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import TemplateForm from '../../../components/admin/createTemplates/TemplateForm';
import { useToast } from '../../../components/ui/notifications/toast-notification.jsx';
import { LoadingSpinner } from '../../../components/ui/feedback/loading-spinner.jsx';
import { auth } from '../../../firebase/firebase';
import { SERVER_URL } from '../../../api/core';
import { useAuth } from '../../../context/auth';
import { ArrowLeft } from 'lucide-react';
import { isUserAdmin } from '../../../utils/admin/adminUtils';

// Create an error boundary wrapper component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Form submission error caught by boundary:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      // Render fallback UI when an error occurs
      return (
        <div className="p-4 bg-red-50 rounded-md dark:bg-red-900/20">
          <h3 className="text-lg font-medium text-red-800 dark:text-red-300">Something went wrong</h3>
          <p className="mt-2 text-sm text-red-700 dark:text-red-400">
            {this.state.error?.message || "An unexpected error occurred. Please try again."}
          </p>
          <button 
            className="mt-3 px-4 py-2 bg-red-100 text-red-800 rounded-md hover:bg-red-200 dark:bg-red-800 dark:text-red-200 dark:hover:bg-red-700"
            onClick={() => this.setState({ hasError: false, error: null })}
          >
            Try Again
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

const CreateTemplates = () => {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { user } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);
  
  // Check if user is the admin
  useEffect(() => {
    const checkAdmin = async () => {
      setIsLoading(true);
      
      try {
        if (user) {
          const userIsAdmin = isUserAdmin(user);
          console.log("User is admin:", userIsAdmin);
          
          if (userIsAdmin) {
            console.log('User authorized as admin');
            setIsAuthorized(true);
          } else {
            console.log('User is not authorized as admin');
            navigate('/app');
          }
        } else if (user === null) {
          console.log('No authenticated user found');
          navigate('/login');
        }
      } catch (error) {
        console.error('Error checking admin status:', error);
        navigate('/app');
      } finally {
        setIsLoading(false);
      }
    };
    
    checkAdmin();
  }, [navigate, user]);
  
  // Reset the page when unmounting to prevent stale state
  useEffect(() => {
    return () => {
      setIsSubmitting(false);
      setSubmitError('');
    };
  }, []);

  // Auto-dismiss error messages after 3 seconds
  useEffect(() => {
    if (submitError) {
      const timer = setTimeout(() => {
        setSubmitError('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [submitError]);

  // Use useCallback to avoid recreating this function on each render
  const handleSubmit = useCallback(async (formData) => {
    if (isSubmitting) {
      console.log('Already submitting, ignoring request');
      return;
    }
    
    setSubmitError('');
    setIsSubmitting(true);
    
    let response, data;
    
    try {
      console.log('Starting submission process...');
      
      // Get the authentication token
      let idToken;
      try {
        idToken = auth.currentUser ? await auth.currentUser.getIdToken() : null;
        
        if (!idToken) {
          throw new Error('Not authenticated. Please log in again.');
        }
        console.log('Auth token acquired successfully');
      } catch (authError) {
        console.error('Authentication error:', authError);
        setSubmitError('Authentication error: ' + (authError.message || 'Failed to get authentication token'));
        setIsSubmitting(false);
        return;
      }
      
      // Make the API request
      try {
        console.log('Submitting to server...');
        const endpoint = `${SERVER_URL}/templateManagement/create`;
        
        response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${idToken}`
          },
          body: formData,
        });
        console.log('Server response received:', response.status);
      } catch (fetchError) {
        console.error('Network error:', fetchError);
        setSubmitError('Network error: Could not connect to server. Please check your connection.');
        setIsSubmitting(false);
        return;
      }

      // Try to parse the response
      try {
        if (response.status === 204 || response.headers.get('content-length') === '0') {
          data = { success: true };
        } else {
          data = await response.json();
        }
        console.log('Response data parsed:', data);
      } catch (parseError) {
        console.error('JSON parse error:', parseError);
        setSubmitError(`Failed to parse server response (Status: ${response.status}). The server may be experiencing issues.`);
        setIsSubmitting(false);
        return;
      }
      
      // Check for response status issues
      if (!response.ok) {
        console.error('Server error response:', response.status, data);
        const errorMessage = data.error || `Failed to create template (Status: ${response.status})`;
        setSubmitError(errorMessage);
        setIsSubmitting(false);
        return;
      }

      // Success case
      console.log('Submission successful!');
      setSubmitError('');
      addToast('Template created successfully!', 'success');
      navigate('/app/private/manageTemplates');
      
    } catch (error) {
      console.error('Unhandled error in submission:', error);
      setSubmitError(error.message || 'An unexpected error occurred');
      setIsSubmitting(false);
    }
  }, [isSubmitting, navigate, addToast]);

  // Show loading state
  if (isLoading) {
    return (
      <div className="p-6 w-full max-w-[1600px] mx-auto">
        <LoadingSpinner message="Checking permissions..." />
      </div>
    );
  }
  
  // If not authorized, redirect happens in the useEffect
  if (!isAuthorized) {
    return null;
  }

  return (
    <div className="p-6 w-full max-w-[1600px] mx-auto">
      {/* Back to Admin Dashboard button */}
      <div className="mb-4">
        <button
          onClick={() => navigate('/app/private/admin')}
          className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
        >
          <ArrowLeft className="w-4 h-4 mr-1.5" />
          Back to Admin
        </button>
      </div>

      <div className="mb-6 flex flex-col space-y-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Create Template</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Fill out the form below to create a new template.
          </p>
        </div>
        <button
          onClick={() => navigate('/app/private/manageTemplates')}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700 dark:hover:bg-gray-700"
        >
          Manage Templates
        </button>
      </div>
      
      <div className="bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg shadow-sm p-6">
        {submitError && (
          <div className="mb-4 rounded-md bg-red-50 p-4 dark:bg-red-900/20">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400 dark:text-red-300" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800 dark:text-red-200">Error Creating Template</h3>
                <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                  {submitError}
                </div>
              </div>
            </div>
          </div>
        )}
        
        <ErrorBoundary>
          <TemplateForm onSubmit={handleSubmit} isSubmitting={isSubmitting} />
        </ErrorBoundary>
      </div>
    </div>
  );
};

export default CreateTemplates;
